import { Ask, Bid, SkuDTO, SkuPageProp } from '@/types/skuDataBackend';
import { Currency } from './currency';

export const getHighestBidReturningBid = (
  arr: (Partial<Bid> & Pick<Bid, 'ownerUserId' | 'value'>)[],
  userId?: string | null
) => {
  if (!Array.isArray(arr)) {
    return null;
  }

  if (arr.length === 0) {
    return null;
  }

  const highestBid = arr
    .sort((a, b) => {
      return b.value - a.value;
    })
    .filter(bid => {
      const ownerId =
        typeof bid.ownerUserId === 'string' ? bid.ownerUserId : String(bid.ownerUserId);
      return ownerId !== userId;
    })[0];

  return highestBid;
};

export const getLowestAskReturningAsk = (
  arr: (Partial<Ask> & Pick<Ask, 'ownerUserId' | 'value'>)[],
  userId?: string | null
) => {
  if (!Array.isArray(arr)) {
    return null;
  }

  if (arr.length === 0) {
    return null;
  }

  const lowestAsk = arr
    .sort((a, b) => {
      return a.value - b.value;
    })
    .filter(ask => {
      const ownerId =
        typeof ask.ownerUserId === 'string' ? ask.ownerUserId : String(ask.ownerUserId);
      return ownerId !== userId;
    })[0];

  return lowestAsk;
};

export const getHighestBid = (
  arr: {
    ownerUserId: string | number | null;
    value: number;
  }[],
  userId?: string | null
) => {
  if (!Array.isArray(arr)) {
    return 0;
  }

  if (arr.length === 0) {
    return 0;
  }

  const bids = userId
    ? arr.filter(bid => {
        const ownerId =
          typeof bid.ownerUserId === 'string' ? bid.ownerUserId : String(bid.ownerUserId);
        return ownerId !== userId;
      })
    : arr;

  const values = bids
    .map(object => object.value)
    .filter(value => typeof value === 'number' && !isNaN(value));

  if (values.length === 0) {
    return 0;
  }
  return Math.max(...values);
};

export const getLowestAsk = (
  arr: {
    ownerUserId: string | number | null;
    value: number;
  }[],
  userId?: string | null
): number => {
  if (!Array.isArray(arr)) {
    return 0;
  }

  if (arr.length === 0) {
    return 0;
  }

  const asks = userId
    ? arr.filter(ask => {
        const ownerId =
          typeof ask.ownerUserId === 'string' ? ask.ownerUserId : String(ask.ownerUserId);
        return ownerId !== userId;
      })
    : arr;

  const values = asks
    .map(object => object.value)
    .filter(value => typeof value === 'number' && !isNaN(value));

  if (values.length === 0) {
    return 0;
  }

  return Math.min(...values);
};

export function toDe(
  value: number | undefined,
  { withSymbol, withDigits }: { withSymbol?: boolean; withDigits?: boolean } = {}
) {
  return Currency.toDe(value, { withSymbol, locale: 'en', withDigits });
}

export function toDeNumeric(value: unknown): number | null {
  const numericValue = Number(value);
  if (typeof numericValue === 'number' && !isNaN(numericValue)) {
    return numericValue && numericValue !== -1 ? numericValue / 100 : null;
  }
  return null;
}

export const getTrendingPercent = (sku: {
  orders: {
    skuOrderStatus: {
      paid: string;
    };
    payValue: number;
  }[];
  retailPrice: number;
}): number => {
  // Verificar si sku.orders es un arreglo
  if (!Array.isArray(sku.orders)) {
    return 0;
  }

  const lastTwoSales = sku.orders.length >= 2;
  const lastSale = sku.orders.length === 1;

  if (lastTwoSales) {
    const arrayOfTwo = sku.orders
      .sort((a, b) => {
        const dateA = a.skuOrderStatus?.paid ? new Date(a.skuOrderStatus.paid).getTime() : 0;
        const dateB = b.skuOrderStatus?.paid ? new Date(b.skuOrderStatus.paid).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, 2);

    if (arrayOfTwo[0].payValue > 0 && arrayOfTwo[1].payValue > 0) {
      const totalSalesValue = (arrayOfTwo[0].payValue / arrayOfTwo[1].payValue - 1) * 100;
      const total = Number(totalSalesValue.toFixed(2));
      return total;
    } else {
      return 0;
    }
  } else if (lastSale && sku.retailPrice && sku.retailPrice > 0) {
    if (sku.orders[0].payValue > 0) {
      const totalSalesValue = (sku.orders[0].payValue / sku.retailPrice - 1) * 100;
      const total = Number(totalSalesValue.toFixed(2));
      return total;
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

export const getTrendingPercentByDateLimit = (
  orders: {
    skuOrderStatus: {
      paid: string;
    };
    payValue: number;
  }[],
  retailPrice: number,
  dateLimit: string
): number => {
  // Verificar si sku.orders es un arreglo
  if (!Array.isArray(orders)) {
    return 0;
  }

  const filteredOrders = orders.filter(order => {
    const paidDate = order.skuOrderStatus?.paid;
    return paidDate && new Date(paidDate).getTime() <= new Date(dateLimit).getTime();
  });

  const lastTwoSales = filteredOrders.length >= 2;
  const lastSale = filteredOrders.length === 1;

  if (lastTwoSales) {
    const arrayOfTwo = filteredOrders
      .sort((a, b) => {
        const dateA = a.skuOrderStatus?.paid ? new Date(a.skuOrderStatus.paid).getTime() : 0;
        const dateB = b.skuOrderStatus?.paid ? new Date(b.skuOrderStatus.paid).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, 2);

    if (arrayOfTwo[0].payValue > 0 && arrayOfTwo[1].payValue > 0) {
      const totalSalesValue = (arrayOfTwo[0].payValue / arrayOfTwo[1].payValue - 1) * 100;
      const total = Number(totalSalesValue.toFixed(2));
      return total;
    } else {
      return 0;
    }
  } else if (lastSale && retailPrice && retailPrice > 0) {
    if (filteredOrders[0].payValue > 0) {
      const totalSalesValue = (filteredOrders[0].payValue / retailPrice - 1) * 100;
      const total = Number(totalSalesValue.toFixed(2));
      return total;
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};
